// (modifié) frontend/src/components/admin/admin_asset_table.tsx
import React, { useEffect, useState } from "react"
import { Card, Statistic, Button, Modal, Space, Row, Col, Alert, Typography, Spin, Empty, Input, Tooltip, Table } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMoneyBillWave, faEye, faUser, faWallet, faCheckCircle, faHourglassHalf, faEdit, faPercentage } from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"
import Swal from "sweetalert2"
import axios from "../../../api/axios"
import { FaUsers } from "react-icons/fa"

const { Title, Text } = Typography

interface UserAvoirData {
  userId: number
  name: string
  email: string
  soldePending: number
  montantARembourser: number
  montantRembourse: number
  countPending: number
  countARembourser: number
  countRembourse: number
  ribDemande: string | null
  bicDemande: string | null
  avoirsPending: any[]
  paymentRate?: number | null
}

const AdminAssetTable: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [usersData, setUsersData] = useState<UserAvoirData[]>([])
  const [selectedRib, setSelectedRib] = useState<string>("")
  const [ribModalVisible, setRibModalVisible] = useState<boolean>(false)
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false)
  const [selectedAvoir, setSelectedAvoir] = useState<any>(null)
  const [newMontant, setNewMontant] = useState<number>(0)
  const [selectedBic, setSelectedBic] = useState<string>("")
  const [searchText, setSearchText] = useState<string>("")

  // NEW: global + per-user rate state
  const [globalPercentage, setGlobalPercentage] = useState<number | null>(null)
  const [globalModalVisible, setGlobalModalVisible] = useState<boolean>(false)
  const [perUserModalVisible, setPerUserModalVisible] = useState<boolean>(false)
  const [perUserPercentage, setPerUserPercentage] = useState<number | null>(null)
  const [savingRate, setSavingRate] = useState<boolean>(false)
  const [perUserTarget, setPerUserTarget] = useState<{
  userId: number
  current?: number | null
} | null>(null)


  const primaryColor = '#2A919D'
  const secondaryColor = '#4BB0C0'
  const accentColor = '#eb8635'

  useEffect(() => {
    fetchUsersWithAvoirs()
    fetchGlobalPercentage()

    // Rafraîchissement périodique + au focus pour garder les données cohérentes
    const interval = setInterval(() => {
      fetchUsersWithAvoirs()
    }, 30000) // toutes les 30s

    const onFocus = () => {
      fetchUsersWithAvoirs()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUsersWithAvoirs()
      }
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  const fetchGlobalPercentage = async (): Promise<void> => {
    try {
      const resp = await axios.get("/avoirs/payment-link-rate")
      const pct = resp.data?.percentage
      setGlobalPercentage(pct != null ? Number(pct) : null)
    } catch (err) {
      // silently ignore, but log
      console.error("Erreur récupération taux global:", err)
    }
  }

  const saveGlobalPercentage = async (): Promise<void> => {
    if (globalPercentage == null || isNaN(Number(globalPercentage)) || Number(globalPercentage) < 0) {
      toast.error("Pourcentage invalide")
      return
    }
    try {
      setSavingRate(true)
      await axios.put("/avoirs/payment-link-rate", { percentage: Number(globalPercentage) })
      toast.success("Taux global mis à jour")
      setGlobalModalVisible(false)
      await fetchGlobalPercentage()
      await fetchUsersWithAvoirs()
    } catch (err) {
      console.error("Erreur update global percentage", err)
      toast.error("Erreur lors de la mise à jour")
    } finally {
      setSavingRate(false)
    }
  }

 const openPerUserModal = (
  userId: number,
  current?: number | null
) => {
  setPerUserTarget({ userId, current })
  setPerUserPercentage(current ?? null)
  setPerUserModalVisible(true)
}


  const savePerUserPercentage = async (): Promise<void> => {
    if (!perUserTarget) return
    if (perUserPercentage != null && (isNaN(Number(perUserPercentage)) || Number(perUserPercentage) < 0)) {
      toast.error("Pourcentage invalide")
      return
    }
    try {
      setSavingRate(true)
      await axios.put(`/avoirs/users/${perUserTarget.userId}/payment-link-rate`, { percentage: perUserPercentage })
      toast.success("Taux utilisateur mis à jour")
      setPerUserModalVisible(false)
      await fetchUsersWithAvoirs()
    } catch (err) {
      console.error("Erreur save per-user", err)
      toast.error("Erreur lors de la sauvegarde")
    } finally {
      setSavingRate(false)
    }
  }

  const fetchUsersWithAvoirs = async (): Promise<void> => {
  setLoading(true)
  try {
    const response = await axios.get("/avoirs/get-avoirs")
    const avoirs = response.data?.data || []

    const usersMap: Record<number, UserAvoirData> = {}

    avoirs.forEach((avoir: any) => {
      const userId = avoir?.annonce?.User?.id
      if (!userId) return

      if (!usersMap[userId]) {
        usersMap[userId] = {
          userId,
          name: `${avoir.annonce?.User?.firstname || ''} ${avoir.annonce?.User?.name || ''}`.trim() || 'Client',
          email: avoir.annonce?.User?.email || '',
          soldePending: 0, // ⚠️ NE PAS utiliser user.avoir_balance ici
          montantARembourser: 0,
          montantRembourse: 0,
          countPending: 0,
          countARembourser: 0,
          countRembourse: 0,
          ribDemande: null,
          bicDemande: null,
          avoirsPending: [],
          paymentRate: avoir.annonce?.User?.payment_link_credit_percentage != null 
            ? Number(avoir.annonce.User.payment_link_credit_percentage) 
            : null
        }
      }

      const montant = Number(avoir.montant_avoir) || 0

      // ✅ CORRECTION : Calculer le solde RÉEL à partir des avoirs "pending"
      if (avoir.statut === 'pending') {
        usersMap[userId].soldePending += montant // Somme des avoirs pending
        usersMap[userId].countPending++
        usersMap[userId].avoirsPending.push(avoir)
      } else if (avoir.statut === 'à_rembourser') {
        usersMap[userId].montantARembourser += montant
        usersMap[userId].countARembourser++
        if (avoir.rib && !usersMap[userId].ribDemande) {
          usersMap[userId].ribDemande = avoir.rib
        }
        if (avoir.bic && !usersMap[userId].bicDemande) {
          usersMap[userId].bicDemande = avoir.bic
        }
      } else if (avoir.statut === 'remboursé') {
        usersMap[userId].montantRembourse += montant
        usersMap[userId].countRembourse++
      }
      // ✅ NE PAS compter les avoirs "utilisés" dans le solde disponible
    })

    setUsersData(Object.values(usersMap).filter(u =>
      u.soldePending > 0 || u.montantARembourser > 0 || u.montantRembourse > 0
    ))
    
    console.log('[ADMIN_ASSET] ✅ Données chargées:', Object.values(usersMap).length, 'utilisateurs')
  } catch (error) {
    console.error("Erreur:", error)
    toast.error("Erreur lors du chargement des données")
  } finally {
    setLoading(false)
  }
}

  const formatBic = (bicValue: string): string => {
    if (!bicValue) return ''
    const cleaned = bicValue.replace(/\s/g, '').toUpperCase()
    if (cleaned.length === 8) {
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)}`
    } else if (cleaned.length === 11) {
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 11)}`
    }
    return cleaned
  }

  const handleShowEditModal = (user: UserAvoirData): void => {
    if (user.avoirsPending.length === 0) {
      toast.warning("Aucun avoir disponible à modifier")
      return
    }

    setSelectedAvoir({
      user,
      avoirs: user.avoirsPending
    })
    setNewMontant(user.soldePending)
    setEditModalVisible(true)
  }

  const handleModifierSolde = async (): Promise<void> => {
    if (!selectedAvoir || newMontant <= 0) {
      toast.error("Montant invalide")
      return
    }

    const result = await Swal.fire({
      title: "Confirmer la modification",
      html: `
      <p><strong>Client:</strong> ${selectedAvoir.user.name}</p>
      <p><strong>Solde actuel:</strong> ${selectedAvoir.user.soldePending.toFixed(2)} €</p>
      <p><strong>Nouveau solde:</strong> ${newMontant.toFixed(2)} €</p>
      <hr />
      <p style="color: ${accentColor};">Cette action modifiera le montant total des avoirs disponibles de ce client.</p>
    `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Oui, modifier",
      cancelButtonText: "Annuler",
      confirmButtonColor: primaryColor,
      cancelButtonColor: '#dc3545'
    })

    if (!result.isConfirmed) return

    try {
      setLoading(true)

      const difference = newMontant - selectedAvoir.user.soldePending

      if (difference === 0) {
        toast.info("Aucune modification nécessaire")
        setEditModalVisible(false)
        return
      }

      if (difference > 0) {
        const firstAvoir = selectedAvoir.avoirs[0]
        await axios.post('/avoirs', {
          annonce_id: firstAvoir.annonce_id,
          montant_avoir: difference,
          statut: 'pending'
        })
      } else {
        let montantARetirer = Math.abs(difference)

        for (const avoir of selectedAvoir.avoirs) {
          if (montantARetirer <= 0) break

          const montantAvoir = Number(avoir.montant_avoir)

          if (montantAvoir <= montantARetirer) {
            await axios.delete(`/avoirs/${avoir.id}`)
            montantARetirer -= montantAvoir
          } else {
            await axios.put(`/avoirs/${avoir.id}`, {
              montant_avoir: montantAvoir - montantARetirer
            })
            montantARetirer = 0
          }
        }
      }

      await Swal.fire({
        icon: "success",
        title: "Solde modifié",
        html: `
        <p>Le solde de <strong>${selectedAvoir.user.name}</strong> a été modifié avec succès</p>
        <p>Ancien solde: ${selectedAvoir.user.soldePending.toFixed(2)} €</p>
        <p>Nouveau solde: ${newMontant.toFixed(2)} €</p>
      `,
        confirmButtonColor: primaryColor
      })

      setEditModalVisible(false)
      await fetchUsersWithAvoirs()
    } catch (error: any) {
      console.error("Erreur modification solde:", error)
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: error.response?.data?.error || error.response?.data?.message || "Erreur lors de la modification",
        confirmButtonColor: accentColor
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRembourserGlobal = async (user: UserAvoirData): Promise<void> => {
    if (user.montantARembourser === 0) {
      toast.info("Aucun montant à rembourser")
      return
    }

    const result = await Swal.fire({
      title: "Confirmer le remboursement global",
      html: `
        <p><strong>Client:</strong> ${user.name}</p>
        <p><strong>Email:</strong> ${user.email}</p>
        <p><strong>Montant total:</strong> ${user.montantARembourser.toFixed(2)} €</p>
        <hr />
        <p style="color: ${accentColor};">Cette action générera les factures et enverra les emails de confirmation.</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Oui, rembourser",
      cancelButtonText: "Annuler",
      confirmButtonColor: primaryColor,
      cancelButtonColor: '#dc3545'
    })

    if (!result.isConfirmed) return

    try {
      setLoading(true)

      console.log('[ADMIN] Traitement remboursement pour userId:', user.userId)

      const response = await axios.post(`/avoirs/traiter-remboursement`, {
        userId: user.userId.toString()
      })

      console.log('[ADMIN] Réponse traitement:', response.data)

      await Swal.fire({
        icon: "success",
        title: "Remboursement effectué",
        html: `
          <p>${user.montantARembourser.toFixed(2)} € remboursé avec succès</p>
          <p style="color: #666;">Les factures ont été générées et les emails envoyés à ${user.email}</p>
        `,
        confirmButtonColor: primaryColor
      })

      // Rafraîchir données
      await fetchUsersWithAvoirs()
    } catch (error: any) {
      console.error("Erreur remboursement:", error)
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: error.response?.data?.error || error.response?.data?.message || "Erreur lors du remboursement",
        confirmButtonColor: accentColor
      })
    } finally {
      setLoading(false)
    }
  }

  const handleShowRib = (rib: string, bic: string): void => {
    const formatRib = (ribValue: string): string => {
      if (!ribValue) return ''
      const cleaned = ribValue.replace(/\s+/g, '')
      if (cleaned.length === 23) {
        return `${cleaned.slice(0, 5)} ${cleaned.slice(5, 10)} ${cleaned.slice(10, 21)} ${cleaned.slice(21, 23)}`
      }
      return cleaned.replace(/(.{4})/g, "$1 ").trim()
    }

    setSelectedRib(formatRib(rib))
    setSelectedBic(bic || '')
    setRibModalVisible(true)
  }

  const totalStats = usersData.reduce((acc, user) => ({
    soldePendingTotal: acc.soldePendingTotal + user.soldePending,
    aRembourserTotal: acc.aRembourserTotal + user.montantARembourser,
    rembourseTotal: acc.rembourseTotal + user.montantRembourse,
    nbClients: acc.nbClients + 1
  }), { soldePendingTotal: 0, aRembourserTotal: 0, rembourseTotal: 0, nbClients: 0 })

  // Filtrer les données en fonction de la recherche
  const filteredData = usersData.filter(user =>
    user.name.toLowerCase().includes(searchText.toLowerCase()) ||
    user.email.toLowerCase().includes(searchText.toLowerCase())
  )

  const columns = [
    {
      title: 'Client',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: UserAvoirData) => (
        <div>
          <div style={{ fontWeight: 'bold', color: primaryColor }}>
            <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px' }} />
            {text}
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>{record.email}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: 6 }}>
            Taux avoir paiement-lien: <strong>{record.paymentRate != null ? `${record.paymentRate}%` : (globalPercentage != null ? `${globalPercentage}% (global)` : '—')}</strong>
          </div>
        </div>
      )
    },
    {
      title: 'Solde disponible',
      dataIndex: 'soldePending',
      key: 'soldePending',
      render: (value: number, record: UserAvoirData) => (
        value > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 'bold', color: secondaryColor }}>
              {value.toFixed(2)} €
            </span>
            <Tooltip title="Modifier le solde">
              <Button
                type="text"
                size="small"
                icon={<FontAwesomeIcon icon={faEdit} />}
                onClick={() => handleShowEditModal(record)}
                style={{ color: primaryColor }}
              />
            </Tooltip>
          </div>
        ) : <span style={{ color: '#ccc' }}>-</span>
      )
    },
    {
      title: 'À rembourser',
      dataIndex: 'montantARembourser',
      key: 'montantARembourser',
      render: (value: number) => (
        value > 0 ? (
          <span style={{ fontWeight: 'bold', color: accentColor }}>
            {value.toFixed(2)} €
          </span>
        ) : <span style={{ color: '#ccc' }}>-</span>
      )
    },
    {
      title: 'Remboursé',
      dataIndex: 'montantRembourse',
      key: 'montantRembourse',
      render: (value: number) => (
        value > 0 ? (
          <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
            {value.toFixed(2)} €
          </span>
        ) : <span style={{ color: '#ccc' }}>-</span>
      )
    },
    {
      title: 'Total',
      key: 'total',
      render: (_: any, record: UserAvoirData) => (
        <span style={{ fontWeight: 'bold', color: primaryColor }}>
          {(record.soldePending + record.montantARembourser + record.montantRembourse).toFixed(2)} €
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: UserAvoirData) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {record.montantARembourser > 0 && record.ribDemande && (
            <>
              <Button
                size="small"
                icon={<FontAwesomeIcon icon={faEye} />}
                onClick={() => handleShowRib(record.ribDemande || '', record.bicDemande || '')}
                style={{ borderColor: primaryColor, color: primaryColor, width: '100%' }}
              >
                Voir RIB/BIC
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                onClick={() => handleRembourserGlobal(record)}
                loading={loading}
                style={{ backgroundColor: secondaryColor, borderColor: secondaryColor, width: '100%' }}
              >
                Rembourser
              </Button>
            </>
          )}

          {/* Button to open per-user rate modal */}
          <Button
  size="small"
  icon={<FontAwesomeIcon icon={faPercentage} />}
  onClick={() => openPerUserModal(record.userId, record.paymentRate)}
  style={{ width: '100%' }}
>
  Définir taux paiement-lien
</Button>

        </Space>
      )
    }
  ]

  if (loading && usersData.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '20px', color: primaryColor }}>Chargement des données...</p>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div className="d-flex align-items-center flex-wrap gap-2">
        <h2 className="gc-user-page-title" style={{ color: "#2a919d", margin: 0 }}>
          <FaUsers className="me-2 gc-user-header-icon" />  Gestion globale des Avoirs
        </h2>
        <p className="gc-user-page-subtitle" style={{ color: "#2a919d" }}>
          Gérez les avoirs des utilisateurs et suivez les remboursements.
        </p>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="Nombre de clients"
              value={totalStats.nbClients}
              prefix={<FontAwesomeIcon icon={faUser} style={{ color: primaryColor }} />}
              valueStyle={{ color: primaryColor }}
            />
          </Card>
        </Col>

        {/* Global percentage card */}
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Tooltip title="Ajouter un taux global pour tous les annonces créer par lien de paiement" placement="bottom">
                  <div style={{ fontSize: 12, color: '#888' }}>Taux global</div>
                </Tooltip>
                <div style={{ fontWeight: 'bold', fontSize: 16 }}>
                  {globalPercentage != null ? `${globalPercentage}%` : 'Non défini'}
                </div>
              </div>
              <div>
                <Button onClick={() => setGlobalModalVisible(true)}>
                  <FontAwesomeIcon icon={faEdit} />
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: '16px' }}>
          <Input.Search
            placeholder="Rechercher par nom ou email..."
            allowClear
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: '100%' }}
            prefix={<FontAwesomeIcon icon={faUser} style={{ color: primaryColor }} />}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="userId"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} clients`
          }}
          locale={{
            emptyText: <Empty description="Aucun client trouvé" />
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Modal pour afficher les coordonnées bancaires */}
      <Modal
        title="Coordonnées bancaires du client"
        open={ribModalVisible}
        onCancel={() => setRibModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRibModalVisible(false)}>
            Fermer
          </Button>
        ]}
      >
        <Alert
          message="RIB et BIC pour remboursement"
          description={
            <div style={{ marginTop: '12px' }}>
              <div style={{ marginBottom: '16px' }}>
                <Text strong>RIB:</Text>
                <div
                  style={{
                    fontFamily: 'Mulish',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    padding: '16px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginTop: '8px'
                  }}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(selectedRib.replace(/\s/g, ''))
                      toast.success("RIB copié dans le presse-papier")
                    } catch {
                      toast.error("Erreur lors de la copie")
                    }
                  }}
                >
                  {selectedRib}
                </div>
              </div>

              {selectedBic && (
                <div>
                  <Text strong>BIC:</Text>
                  <div
                    style={{
                      fontFamily: 'Mulish',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      padding: '16px',
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginTop: '8px'
                    }}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(selectedBic.replace(/\s/g, ''))
                        toast.success("BIC copié dans le presse-papier")
                      } catch {
                        toast.error("Erreur lors de la copie")
                      }
                    }}
                  >
                    {formatBic(selectedBic)}
                  </div>
                </div>
              )}

              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px', textAlign: 'center' }}>
                Cliquez pour copier
              </Text>
            </div>
          }
          type="info"
          showIcon
        />
      </Modal>

      {/* Modal pour modifier le solde */}
      <Modal
        title="Modifier le solde disponible"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)} disabled={loading}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleModifierSolde}
            loading={loading}
            disabled={!newMontant || newMontant < 0}
            style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
          >
            Confirmer la modification
          </Button>
        ]}
      >
        {selectedAvoir && (
          <div>
            <Alert
              message="Modification du solde"
              description={`Vous allez modifier le solde disponible de ${selectedAvoir.user.name}`}
              type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
            />

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Client: </Text>
              <Text>{selectedAvoir.user.name}</Text>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Email: </Text>
              <Text>{selectedAvoir.user.email}</Text>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Text strong>Solde actuel: </Text>
              <Text style={{ fontSize: '16px', color: secondaryColor }}>
                {selectedAvoir.user.soldePending.toFixed(2)} €
              </Text>
            </div>

            <div>
              <Text strong>Nouveau solde (€):</Text>
              <Input
                type="number"
                value={newMontant}
                onChange={(e) => setNewMontant(parseFloat(e.target.value) || 0)}
                min={0}
                step={0.001}
                style={{ marginTop: '8px', fontSize: '16px' }}
                disabled={loading}
              />
            </div>

            <Alert
              message="Information"
              description="Cette modification ajustera automatiquement les avoirs disponibles du client"
              type="info"
              showIcon
              style={{ marginTop: '16px' }}
            />
          </div>
        )}
      </Modal>

      {/* Global rate modal */}
      <Modal
        title="Modifier taux global - paiement par lien (%)"
        open={globalModalVisible}
        onCancel={() => setGlobalModalVisible(false)}
        onOk={saveGlobalPercentage}
        confirmLoading={savingRate}
      >
        <Input
          value={globalPercentage ?? ''}
          onChange={(e) => setGlobalPercentage(e.target.value === '' ? null : parseFloat(e.target.value))}
          placeholder="Ex: 5.00"
        />
      </Modal>

      {/* Per-user rate modal */}
      <Modal
        title={perUserTarget ? `Taux user ${perUserTarget.userId}` : 'Taux utilisateur'}
        open={perUserModalVisible}
        onCancel={() => setPerUserModalVisible(false)}
        onOk={savePerUserPercentage}
        confirmLoading={savingRate}
      >
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: '#888' }}>Valeur actuelle: {perUserTarget?.current != null ? `${perUserTarget.current}%` : '— (utilise global)'}</div>
        </div>
        <Input
          value={perUserPercentage ?? ''}
          onChange={(e) => setPerUserPercentage(e.target.value === '' ? null : parseFloat(e.target.value))}
          placeholder="Ex: 7.5 (laisser vide pour utiliser le taux global)"
        />
        <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
          Laisser vide pour utiliser le taux global.
        </div>
      </Modal>
    </div>
  )
}

export default AdminAssetTable